import React, { useCallback, useEffect, useState } from "react";
import { Layout } from "../../layouts/DashboardLayout";
import useToken from "../../../../hooks/auth/useToken";
import {
  cmrChinaTransferService,
  CmrChinaAttachmentKind,
  CmrChinaOperationType,
  CmrChinaPayoutStatus,
  CmrChinaTransfer,
} from "../../services/cmrChinaTransferService";
import {
  AlertCircle,
  CheckCircle,
  FileText,
  Loader2,
  PlayCircle,
  RefreshCw,
  Search,
  Send,
  XCircle,
  Ban,
} from "lucide-react";

const PAGE_SIZE = 25;

const STATUS_LABEL: Record<CmrChinaPayoutStatus, string> = {
  PENDING_MANUAL_PAYOUT: "À payer",
  IN_PROGRESS: "Payout en cours",
  COMPLETED: "Livré",
  FAILED: "Échoué (remboursé)",
  CANCELED: "Annulé (remboursé)",
};

const STATUS_BADGE: Record<CmrChinaPayoutStatus, string> = {
  PENDING_MANUAL_PAYOUT: "bg-yellow-100 text-yellow-700",
  IN_PROGRESS: "bg-blue-100 text-blue-700",
  COMPLETED: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-700",
  CANCELED: "bg-gray-100 text-gray-700",
};

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  ALIPAY: "Alipay",
  WECHAT_PAY: "WeChat Pay",
  UNIONPAY: "UnionPay",
  ECNY: "e-CNY",
};

const ATTACHMENTS: { kind: CmrChinaAttachmentKind; flag: keyof CmrChinaTransfer["details"]; label: string }[] = [
  { kind: "payment_qr", flag: "has_payment_qr", label: "QR de paiement" },
  { kind: "invoice", flag: "has_invoice", label: "Facture" },
  { kind: "contact_doc", flag: "has_contact_doc", label: "Contrat / bon de commande" },
];

/** Actions proposées selon le statut courant (les statuts finaux n'en ont pas). */
const NEXT_ACTIONS: Partial<Record<CmrChinaPayoutStatus, CmrChinaPayoutStatus[]>> = {
  PENDING_MANUAL_PAYOUT: ["IN_PROGRESS", "COMPLETED", "FAILED", "CANCELED"],
  IN_PROGRESS: ["COMPLETED", "FAILED", "CANCELED"],
};

const ACTION_UI: Record<string, { label: string; className: string; icon: React.ElementType; askNote: boolean }> = {
  IN_PROGRESS: {
    label: "Démarrer le payout",
    className: "bg-blue-600 hover:bg-blue-700",
    icon: PlayCircle,
    askNote: false,
  },
  COMPLETED: {
    label: "Marquer livré",
    className: "bg-green-600 hover:bg-green-700",
    icon: CheckCircle,
    askNote: false,
  },
  FAILED: { label: "Échec et rembourser", className: "bg-red-600 hover:bg-red-700", icon: XCircle, askNote: true },
  CANCELED: { label: "Annuler et rembourser", className: "bg-gray-600 hover:bg-gray-700", icon: Ban, askNote: true },
};

const money = (value: number | null | undefined, currency: string) =>
  `${Number(value ?? 0).toLocaleString("fr-CA", { maximumFractionDigits: 2 })} ${currency}`;

const errorMessage = (error: unknown) => {
  const detail = (error as { response?: { data?: { detail?: unknown; message?: unknown } } })?.response?.data;
  return String(detail?.detail ?? detail?.message ?? "Erreur inattendue.");
};

const CmrChinaTransfersPage: React.FC = () => {
  const { token } = useToken();
  const [items, setItems] = useState<CmrChinaTransfer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  const [status, setStatus] = useState<CmrChinaPayoutStatus | "">("PENDING_MANUAL_PAYOUT");
  const [operationType, setOperationType] = useState<CmrChinaOperationType | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const fetchTransfers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cmrChinaTransferService.list(token, {
        status: status || undefined,
        operation_type: operationType || undefined,
        search: search || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [token, status, operationType, search, page]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const applyFilter =
    <T,>(setter: (v: T) => void) =>
    (value: T) => {
      setPage(0);
      setter(value);
    };

  const handleAction = async (item: CmrChinaTransfer, next: CmrChinaPayoutStatus) => {
    if (!token) return;
    const ui = ACTION_UI[next];
    const ref = item.transfert?.txt_reference ?? item.details.id;
    let note: string | undefined;
    if (ui.askNote) {
      const answer = window.prompt(
        `${ui.label} — ${ref}\nLe client est remboursé sur son wallet et reçoit cette note par email (optionnelle) :`
      );
      if (answer === null) return;
      note = answer.trim() || undefined;
    } else if (!window.confirm(`${ui.label} pour ${ref} ?`)) {
      return;
    }

    setActionId(item.details.id);
    try {
      await cmrChinaTransferService.updateStatus(token, item.details.id, next, note);
      await fetchTransfers();
    } catch (err) {
      alert(errorMessage(err));
    } finally {
      setActionId(null);
    }
  };

  const openAttachment = async (item: CmrChinaTransfer, kind: CmrChinaAttachmentKind) => {
    if (!token) return;
    try {
      await cmrChinaTransferService.openAttachment(token, item.details.id, kind);
    } catch (err) {
      alert(errorMessage(err));
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="flex gap-3 items-center mb-2 text-3xl font-bold text-gray-800">
          <Send className="w-8 h-8 text-green-600" />
          Transferts Cameroun → Chine
        </h1>
        <p className="text-gray-600">
          Payouts manuels vers des particuliers et des fournisseurs en Chine. Un échec ou une annulation rembourse le
          wallet du client.
        </p>
      </div>

      <div className="flex flex-col gap-3 p-4 mb-6 bg-white rounded-lg border border-gray-200 lg:flex-row lg:items-center">
        <select
          value={status}
          onChange={(e) => applyFilter(setStatus)(e.target.value as CmrChinaPayoutStatus | "")}
          className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
          aria-label="Statut"
        >
          <option value="">Tous les statuts</option>
          {(Object.keys(STATUS_LABEL) as CmrChinaPayoutStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          value={operationType}
          onChange={(e) => applyFilter(setOperationType)(e.target.value as CmrChinaOperationType | "")}
          className="px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
          aria-label="Type d'opération"
        >
          <option value="">Particuliers et fournisseurs</option>
          <option value="INDIVIDUAL">Particuliers</option>
          <option value="SUPPLIER">Fournisseurs</option>
        </select>
        <form
          className="flex flex-1 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            applyFilter(setSearch)(searchInput.trim());
          }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Référence, bénéficiaire, entreprise, téléphone, email client…"
              className="py-2 pr-3 pl-9 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700">
            Rechercher
          </button>
        </form>
        <button
          onClick={fetchTransfers}
          className="flex gap-2 justify-center items-center px-4 py-2 text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Actualiser
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        {loading && items.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-12 h-12 text-green-600 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col justify-center items-center h-64 text-red-600">
            <AlertCircle className="mb-4 w-12 h-12" />
            <p className="font-medium">Chargement impossible : {error}</p>
            <button onClick={fetchTransfers} className="mt-3 text-sm underline">
              Réessayer
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-64 text-gray-500">
            <AlertCircle className="mb-4 w-12 h-12" />
            <p className="text-lg font-medium">Aucun transfert pour ces filtres</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {items.map((item) => {
              const { transfert: t, details: d, sender } = item;
              const isSupplier = d.operation_type === "SUPPLIER";
              const busy = actionId === d.id;
              const status = d.status ?? "PENDING_MANUAL_PAYOUT";
              const address = [d.street, d.unit, d.city, d.province, d.recipient_address].filter(Boolean).join(", ");
              return (
                <div key={d.id} className="p-6 transition-colors hover:bg-gray-50">
                  <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start">
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="flex flex-wrap gap-3 items-center">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {money(t?.total_amount, "XAF")} → {money(t?.receiving_amount, "CNY")}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[status]}`}>
                          {STATUS_LABEL[status]}
                        </span>
                        <span className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                          {isSupplier ? "Fournisseur" : "Particulier"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        Référence : <span className="font-mono">{t?.txt_reference}</span> · Taux {t?.conversion_rate} ·
                        Frais {money(t?.fee, "XAF")}
                      </p>

                      <div className="grid gap-x-8 gap-y-1 text-sm text-gray-700 md:grid-cols-2">
                        <p>
                          <span className="font-medium">Bénéficiaire :</span>{" "}
                          {isSupplier && d.company_name ? `${d.company_name} (${d.recipient_name})` : d.recipient_name}
                        </p>
                        <p>
                          <span className="font-medium">Téléphone :</span> {d.recipient_phone || "—"}
                        </p>
                        <p className="md:col-span-2">
                          <span className="font-medium">Adresse :</span> {address}
                        </p>
                        {d.recipient_email && (
                          <p>
                            <span className="font-medium">Email :</span> {d.recipient_email}
                          </p>
                        )}
                        {isSupplier && (
                          <>
                            <p>
                              <span className="font-medium">Payer via :</span>{" "}
                              {PAYMENT_METHOD_LABEL[d.payment_method ?? ""] ?? d.payment_method} — {d.payment_account}
                            </p>
                            <p>
                              <span className="font-medium">Facture :</span> {d.invoice_number}
                              {d.invoice_date && ` du ${new Date(d.invoice_date).toLocaleDateString("fr-CA")}`}
                            </p>
                          </>
                        )}
                        <p className="md:col-span-2">
                          <span className="font-medium">Client :</span> {sender?.name || "—"}
                          {sender?.email && ` · ${sender.email}`} · {d.sender_phone}
                        </p>
                        {t?.description && (
                          <p className="md:col-span-2">
                            <span className="font-medium">Motif :</span> {t.description}
                          </p>
                        )}
                        {d.payout_note && (
                          <p className="md:col-span-2">
                            <span className="font-medium">Note admin :</span> {d.payout_note}
                          </p>
                        )}
                      </div>

                      {ATTACHMENTS.some((a) => d[a.flag]) && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {ATTACHMENTS.filter((a) => d[a.flag]).map((a) => (
                            <button
                              key={a.kind}
                              onClick={() => openAttachment(item, a.kind)}
                              className="flex gap-1 items-center px-3 py-1 text-xs text-gray-700 rounded-full border border-gray-300 hover:bg-gray-100"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}

                      <p className="text-xs text-gray-500">
                        Créé le {new Date(d.created_at).toLocaleString("fr-CA")}
                        {d.payout_processed_at &&
                          ` · traité le ${new Date(d.payout_processed_at).toLocaleString("fr-CA")}`}
                      </p>
                    </div>

                    {NEXT_ACTIONS[status] && (
                      <div className="flex flex-col gap-2 sm:flex-row lg:flex-col lg:w-56">
                        {NEXT_ACTIONS[status]!.map((next) => {
                          const ui = ACTION_UI[next];
                          const Icon = ui.icon;
                          return (
                            <button
                              key={next}
                              onClick={() => handleAction(item, next)}
                              disabled={busy}
                              className={`flex gap-2 justify-center items-center px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${ui.className}`}
                            >
                              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                              {ui.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
          <span>
            {total} transferts · page {page + 1} / {pageCount}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
            >
              Précédent
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={page + 1 >= pageCount || loading}
              className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CmrChinaTransfersPage;
